import { getPackageProviderIconUrl } from 'components/shared/ppoints/PackageProviderIcon';
import { ordersUrl } from 'features/orders/utils/ordersUrl';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import PackageProviderService from 'services/settings/ppoints/PackageProviderService';
import { useMostUsedProvider } from '../../hooks/useMostUsedProvider';

// Same shape as the loaded row: the 36px logo, the name, and the statistic at the end of the line
const MostUsedProviderSkeleton: React.FC = () => (
    <div className="flex items-center gap-3">
        <div className="skeleton-block size-9 shrink-0 rounded-lg" />
        <div className="skeleton-block h-5 w-24 rounded-sm" />
        <div className="skeleton-block ml-auto h-5 w-20 rounded-sm" />
    </div>
);

// The most used carrier of the last 30 days with its logo, its order count and the total across every
// carrier — or "No orders yet" when there weren't any. The logo is a plain 36px image, since
// `s-thumbnail` (`PackageProviderIcon`) has no 36px size.
export const MostUsedProvider: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { provider, count, total, isLoading } = useMostUsedProvider();

    if (isLoading) return <MostUsedProviderSkeleton />;

    if (!provider) {
        return (
            <div className="py-4">
                <s-text color="subdued">{t('DASHBOARD.PLAN_OVERVIEW_DETAILS.CARRIERS.NO_ORDERS_YET')}</s-text>
            </div>
        );
    }

    // Opens the orders page filtered to this carrier
    return (
        <button
            type="button"
            className="flex w-full cursor-pointer items-center gap-3 text-left"
            onClick={() => navigate(ordersUrl(provider))}
        >
            <img
                className="size-9 shrink-0 rounded-lg border border-border"
                src={getPackageProviderIconUrl(provider)}
                alt={PackageProviderService.getDisplayName(provider) || provider}
            />
            <div className="min-w-0 flex-1 truncate">
                <s-text type="strong">{PackageProviderService.getDisplayName(provider) || provider}</s-text>
            </div>
            <div className="shrink-0">
                <s-text color="subdued">
                    {t('DASHBOARD.PLAN_OVERVIEW_DETAILS.CARRIERS.MOST_USED_COUNT', { count, total })}
                </s-text>
            </div>
        </button>
    );
};
